import { cn } from '#utils';

/**
 * ===========================================================================
 * 🔴 DIE WORTMARKE — Blatt 11 · Bauteil 11, Befund 551/578
 * ===========================================================================
 *
 * Blatt 11 (20.09.2026) fuehrt sie als FEHLENDES Bauteil, und der Befund stimmt
 * fuer das System, nicht fuer den Baum:
 *
 *   „KitUI hat kein Markenzeichen. Wordmark gibt es nur in LocariWeb — dem
 *    Marketing-System —, und von dort holt die App nichts (551). Deshalb steht
 *    es hier."
 *
 * Gemessen am 20.09.2026 lagen die Glyphpfade an ZWEI Stellen in `apps/web`
 * (`components/app-logo.tsx` und `app/(marketing)/_components/wordmark.tsx`) und
 * an keiner in `packages/ui`. Die App war also versorgt — aber aus ihrem eigenen
 * Anwendungsordner heraus, und damit kopierbar. Diese Datei ist der eine Ort.
 *
 * ---------------------------------------------------------------------------
 * 🔴 NIE ALS TEXT NACHGEBAUT, NIE NACHGETIPPT.
 *
 * Blatt 11: „Das war in diesem Projekt schon zweimal ein Befund —
 * `<span>LOCARI</span>` ist kein Markenzeichen, es ist ein Wort in der
 * Systemschrift." Die Pfade unten sind die Inter-600-Glyphen, aus denen auch
 * `public/images/locari-wordmark.svg` besteht; sie stehen hier inline, weil die
 * Maske (der diagonale Schnitt im O) zur Laufzeit eine eigene `mask`-Kennung
 * braucht und ein `<img>` die Farbe nicht erbt.
 *
 * ---------------------------------------------------------------------------
 * 🔴 DREI GROESSEN, KEINE VIERTE — und `groesse` ist trotzdem freiwillig.
 *
 * Blatt 11 nennt 110×35 (Anmeldung) · 100×32 (Kopfzeile) · 92×29 (Telefon) und
 * dazu: „Wer eine andere braucht, meldet einen Befund."
 *
 * ⚠️ Ohne `groesse` rendert dieses Bauteil exakt den Bestandsbaum von
 * `AppLogo` — dieselbe Klassenfolge, dieselbe `viewBox`, dieselbe
 * Maskenkennung. Das ist Absicht und folgt der DS-Regel 6 („eine Erweiterung
 * darf den Render-Output bestehender Flaechen nicht aendern"): die neun
 * Aufrufstellen im Bestand geben ihre Groesse bis heute ueber `className`
 * (`h-8 w-auto`, `h-[33px]`) vor. Sie auf `groesse` umzustellen ist ein
 * eigener, benannter Schritt — kein Nebeneffekt dieses Umzugs.
 */

/**
 * Inter 600, „LOCARI" — aus opentype.js gezogen, nicht abgetippt.
 *
 * 🔴 EXPORTIERT SEIT DEM 20.09.2026 — Design-Anweisung 577, „Eine Datei, nicht
 * zwoelf Kopien":
 *
 *   „Die schlechte [Nachricht]: zwoelf Kopien sind zwoelf Stellen, an denen
 *    ein Markenwechsel vergessen werden kann. […] Es gilt: Die SVG-Datei ist
 *    die einzige Quelle. Kein Blatt und kein Bauteil traegt den Pfad inline."
 *
 * Fuer die App ist „inline" hier unvermeidbar (Maskenkennung zur Laufzeit,
 * `currentColor`) — der Punkt der Anweisung ist die ZAHL der Stellen, und die
 * ist ab hier eins. `app/(marketing)/_components/wordmark.tsx` trug bis heute
 * eine zweite, zeichengleiche Fassung und zieht sie jetzt von hier.
 *
 * Gehalten wird das von `check-marken-bestand.mjs`, Pruefung C.
 */
export const LOCARI_PFAD =
  'M41.99 80L5.86 80L5.86 21.80L16.29 21.80L16.29 71.21L41.99 71.21L41.99 80Z M80.02 80.78Q72.36 80.78 66.30 77.23Q60.25 73.67 56.75 66.99Q53.26 60.31 53.26 50.94L53.26 50.94Q53.26 41.52 56.75 34.82Q60.25 28.13 66.30 24.57Q72.36 21.02 80.02 21.02L80.02 21.02Q87.63 21.02 93.67 24.57Q99.70 28.13 103.20 34.82Q106.70 41.52 106.70 50.94L106.70 50.94Q106.70 60.31 103.20 67.01Q99.70 73.71 93.67 77.25Q87.63 80.78 80.02 80.78ZM80.02 71.45Q84.74 71.45 88.39 69.08Q92.05 66.72 94.12 62.13Q96.19 57.54 96.19 50.94L96.19 50.94Q96.19 44.26 94.12 39.67Q92.05 35.08 88.39 32.71Q84.74 30.35 80.02 30.35L80.02 30.35Q75.25 30.35 71.58 32.73Q67.91 35.12 65.82 39.71Q63.73 44.30 63.73 50.94L63.73 50.94Q63.73 57.54 65.82 62.11Q67.91 66.68 71.58 69.06Q75.25 71.45 80.02 71.45Z M145.38 80.78Q137.73 80.78 131.71 77.23Q125.70 73.67 122.22 66.99Q118.74 60.31 118.74 50.94L118.74 50.94Q118.74 41.52 122.24 34.82Q125.73 28.13 131.77 24.57Q137.80 21.02 145.38 21.02L145.38 21.02Q150.15 21.02 154.31 22.36Q158.47 23.71 161.73 26.31Q164.99 28.91 167.10 32.66Q169.21 36.41 169.91 41.17L169.91 41.17L159.41 41.17Q158.90 38.55 157.67 36.56Q156.44 34.57 154.60 33.18Q152.77 31.80 150.46 31.07Q148.16 30.35 145.54 30.35L145.54 30.35Q140.77 30.35 137.08 32.75Q133.39 35.16 131.30 39.75Q129.21 44.34 129.21 50.94L129.21 50.94Q129.21 57.62 131.32 62.19Q133.43 66.76 137.12 69.10Q140.81 71.45 145.50 71.45L145.50 71.45Q148.12 71.45 150.42 70.72Q152.73 70 154.58 68.61Q156.44 67.23 157.69 65.21Q158.94 63.20 159.41 60.63L159.41 60.63L169.95 60.63Q169.37 64.80 167.39 68.46Q165.42 72.11 162.22 74.88Q159.02 77.66 154.78 79.22Q150.54 80.78 145.38 80.78Z M191.02 80L179.62 80L199.93 21.80L213.17 21.80L233.95 80L222.51 80L217.59 65.59L195.79 65.59L191.02 80ZM198.56 57.19L214.70 57.19L212.16 49.77Q210.63 45.08 208.99 39.14L208.99 39.14Q207.82 34.92 206.45 29.88L206.45 29.88Q205.16 35 204.03 39.26L204.03 39.26Q202.43 45.23 201.02 49.77L201.02 49.77L198.56 57.19Z M256.16 80L245.73 80L245.73 21.80L267.48 21.80Q274.16 21.80 278.73 24.14Q283.30 26.48 285.65 30.68Q287.99 34.88 287.99 40.43Q287.99 45.98 285.63 50.08Q283.27 54.18 278.66 56.41L278.66 56.41Q278.11 56.68 277.52 56.91L277.52 56.91L290.14 80L278.46 80L267.02 58.63L256.16 58.63L256.16 80ZM256.16 30.55L256.16 50L265.96 50Q269.91 50 272.41 48.89Q274.91 47.77 276.12 45.63Q277.33 43.48 277.33 40.43L277.33 40.43Q277.33 37.30 276.12 35.10Q274.91 32.89 272.39 31.72Q269.87 30.55 265.88 30.55L265.88 30.55L256.16 30.55Z M301.91 21.80L312.34 21.80L312.34 80L301.91 80L301.91 21.80Z';

/**
 * Der diagonale Schnitt im O — ein Apostroph-Schnitt, keine Zierde.
 *
 * Exportiert aus demselben Grund wie {@link LOCARI_PFAD} (577): ohne die Maske
 * fehlt die Aussparung, und genau das war einer der vier Schaeden, die 578 im
 * Bestand gefunden hat („Keine Maske, also fehlte die diagonale Aussparung im
 * o").
 */
export const SCHNITT_PFAD =
  'M 69.17 46.00 L 50.85 27.68 L 58.66 19.87 L 76.98 38.19 Z';

/** Nur das O, mittig in 32×32 — die eingeklappte Seitenleiste und das Favicon. */
const O_KOMPAKT_PFAD =
  'M16.01 26.46Q13.33 26.46 11.21 25.21Q9.10 23.97 7.87 21.63Q6.65 19.29 6.65 16.01L6.65 16.01Q6.65 12.72 7.87 10.37Q9.10 8.03 11.21 6.79Q13.33 5.54 16.01 5.54L16.01 5.54Q18.68 5.54 20.79 6.79Q22.90 8.03 24.13 10.37Q25.35 12.72 25.35 16.01L25.35 16.01Q25.35 19.29 24.13 21.64Q22.90 23.98 20.79 25.22Q18.68 26.46 16.01 26.46ZM16.01 23.19Q17.67 23.19 18.95 22.36Q20.22 21.54 20.95 19.93Q21.67 18.32 21.67 16.01L21.67 16.01Q21.67 13.68 20.95 12.07Q20.22 10.46 18.95 9.64Q17.67 8.81 16.01 8.81L16.01 8.81Q14.35 8.81 13.06 9.64Q11.78 10.48 11.04 12.08Q10.31 13.69 10.31 16.01L10.31 16.01Q10.31 18.32 11.04 19.92Q11.78 21.52 13.06 22.36Q14.35 23.19 16.01 23.19Z';

const O_KOMPAKT_SCHNITT = 'M 12.2 14.3 L 5.8 7.9 L 8.5 5.1 L 15.0 11.6 Z';

/**
 * Die drei Groessen aus Blatt 11 — und nur diese drei.
 *
 * 🔴 Sie stehen als feste `width`/`height` und nicht als Klassen: Blatt 11 sagt
 * „nie nachtraeglich eingefaerbt, nie gedreht, nie gestaucht". Eine Klasse, die
 * nur die Breite setzt, ueberlaesst die Hoehe dem Seitenverhaeltnis der
 * `viewBox` — das geht gut, bis jemand daneben ein `h-*` schreibt.
 */
export const WORTMARKE_GROESSEN = {
  /** Telefon — Blatt 11: 92 × 29. */
  klein: { width: 92, height: 29 },
  /** Kopfzeile — Blatt 11: 100 × 32. */
  normal: { width: 100, height: 32 },
  /** Anmeldung — Blatt 11: 110 × 35. */
  gross: { width: 110, height: 35 },
} as const;

export type WortmarkenGroesse = keyof typeof WORTMARKE_GROESSEN;

export function Wortmarke({
  groesse,
  variant = 'default',
  kompakt = false,
  className,
}: {
  groesse?: WortmarkenGroesse;
  variant?: 'default' | 'light';
  kompakt?: boolean;
  className?: string;
}) {
  if (kompakt) {
    return <WortmarkeKompakt className={className} variant={variant} />;
  }

  // AUSNAHME (Job 2): Fuellung der Wortmarke. `variant="light"` ist die
  // Invers-Darstellung auf dunklem Grund — reines Weiss ist Teil der Marke,
  // kein Oberflaechenton, und laut Zuordnungsregeln (Schwarz/Weiss) ohnehin
  // ausgenommen. Hier ist der Wert tragend: ohne gesetzte Textfarbe wuerde
  // `currentColor` erben statt zu invertieren.
  const farbe = variant === 'light' ? '#ffffff' : 'currentColor';
  const maskenKennung = 'locari-cut';
  const masse = groesse ? WORTMARKE_GROESSEN[groesse] : null;

  return (
    <svg
      // Ohne `groesse` genau die Bestandsklasse von `AppLogo` — siehe Kopf.
      className={cn(masse ? null : 'w-[100px] lg:w-[110px]', className, {
        'text-primary': variant === 'default',
        'text-white': variant === 'light',
      })}
      width={masse?.width}
      height={masse?.height}
      viewBox="-4 -4 326 104"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <mask id={maskenKennung}>
          <rect x="-4" y="-4" width="326" height="104" fill="white" />
          <path d={SCHNITT_PFAD} fill="black" />
        </mask>
      </defs>
      <path d={LOCARI_PFAD} fill={farbe} mask={`url(#${maskenKennung})`} />
    </svg>
  );
}

function WortmarkeKompakt({
  className,
  variant = 'default',
}: {
  className?: string;
  variant?: 'default' | 'light';
}) {
  const farbe = variant === 'light' ? '#ffffff' : 'currentColor';
  const maskenKennung = 'locari-fav-cut';

  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('h-8 w-8', className)}
    >
      <defs>
        <mask id={maskenKennung}>
          <rect width="32" height="32" fill="white" />
          <path d={O_KOMPAKT_SCHNITT} fill="black" />
        </mask>
      </defs>
      <path d={O_KOMPAKT_PFAD} fill={farbe} mask={`url(#${maskenKennung})`} />
    </svg>
  );
}
